import React, { useEffect, useRef, useState } from "react";

import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import { Eyebrow } from "./Panel";
import { useTranslation } from "react-i18next";

/**
 * Add a tax or accessorial line that isn't already on the invoice.
 *
 * Taxes and accessorials are identified by NameEn, not by a fixed catalogue —
 * the backend accepts any name — so this stays free text. A fixed dropdown
 * would silently drop any line the list didn't anticipate.
 *
 * The Id is assigned by the caller as max(existing) + 1, matching NewTax.jsx
 * and AdditionalCharge.jsx.
 */
const NewNamedChargeModal = ({ kind, onClose, onAdd }) => {
  const { t } = useTranslation("admin");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  // One line per opening. The Fade keeps this modal clickable for ~195ms
  // after it closes; production's modal (a plain div, no transition) is gone
  // on the next render, so a double-click there adds exactly one line.
  const added = useRef(false);

  useEffect(() => {
    if (kind) {
      setName("");
      setAmount("");
      added.current = false;
    }
  }, [kind]);

  const isTax = kind === "tax";

  /* No validity gate. Production's handleSave has none (NewTax.jsx:76-101,
     AdditionalCharge.jsx:76-101) despite the "*" on its labels: a blank name
     or a blank amount is accepted. A blank amount leaves an empty line that
     the save filter drops; a blank name with an amount is sent with an empty
     NameEn. Matches Classic, see COVERAGE.md. */
  const handleAdd = () => {
    if (!kind || added.current) return;
    added.current = true;
    onAdd({ name, amount });
  };

  return (
    <Modal
      open={!!kind}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={!!kind}>
        <div className="fixed inset-0 flex items-center justify-center p-4 outline-none">
          <div className="w-full max-w-[380px] overflow-hidden rounded-[9px] border border-ui-border bg-ui-surface shadow-ui-md">
            <div className="border-b border-ui-border bg-ui-surface-sunken px-3 py-2">
              <span className="font-jakarta text-[12px] font-extrabold text-ui-text">
                {isTax ? t("invoice.addATaxLine") : t("invoice.addAChargeLine")}
              </span>
            </div>

            <div className="flex flex-col gap-2.5 px-3 py-3">
              <div className="flex flex-col gap-1">
                <Eyebrow>{isTax ? t("invoice.taxName") : t("invoice.chargeName")}</Eyebrow>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={isTax ? t("invoice.egGst") : t("invoice.egSurcharge")}
                  className="rounded-[5px] border border-ui-border-strong bg-ui-surface px-2 py-1.5 font-manrope text-[11.5px] font-medium text-ui-text"
                />
              </div>

              <div className="flex flex-col gap-1">
                <Eyebrow>{t("invoice.amount")}</Eyebrow>
                {/* type="number", as production's amount input
                    (NewTax.jsx:136-143): the browser yields "" for anything
                    that is not a number, so "$12.50" cannot leave the modal. */}
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="rounded-[5px] border border-ui-border-strong bg-ui-surface px-2 py-1.5 text-right font-manrope text-[11.5px] font-medium tabular-nums text-ui-text"
                />
              </div>

              <p className="m-0 font-manrope text-[10px] font-medium leading-relaxed text-ui-text-muted">
                {t("invoice.nameIsIdentity")}
              </p>
            </div>

            <div className="flex justify-end gap-2 border-t border-ui-border bg-ui-surface-sunken px-3 py-2">
              <button
                onClick={onClose}
                className="rounded-[5px] border border-ui-border bg-ui-surface px-3 py-1.5 font-manrope text-[11px] font-semibold text-ui-text hover:bg-ui-row-hover"
              >
                {t("common:actions.cancel")}
              </button>
              <button
                /* Untrimmed, matching production's line identity. Trimming could
                   turn " GST" into an existing catalogue name and emit two
                   tax_charges rows for it — a doubled tax from a stray space. */
                onClick={handleAdd}
                className="rounded-[5px] border border-state-success-solid bg-state-success-solid px-3 py-1.5 font-manrope text-[11px] font-semibold text-brand-contrast hover:bg-state-success-solid-hover disabled:opacity-50"
              >
                {t("invoice.addLine")}
              </button>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default NewNamedChargeModal;
